import * as React from 'react';
import {Dispatch, useRef, useState} from "react";

type ContactFormModel = {
    setStep: Dispatch<any>,
    isCompany: boolean,
    setIsCompany: Dispatch<any>,
    name: string,
    setName: Dispatch<any>,
    phone: string,
    setPhone: Dispatch<any>,
    email: string,
    setEmail: Dispatch<any>,
    companyName: string,
    setCompanyName: Dispatch<any>,
    companyCode: string,
    setCompanyCode: Dispatch<any>,
    taxCode: string,
    setTaxCode: Dispatch<any>,
    deliveryDate: string,
    elevatorExist: boolean,
    deliveryTime: number,
    notes: string,
    trashNearHouse: boolean,
    floor: number,
    address: string,
    selectedCity: string,
    selectedSize: string,
    deliveryTimeLabel: string,
    bagsCount: number,
    infoAboutBags: string
    apiUrl: string,
    trashesPhotos: any,
    trashStacksCount: number,
    tags: [],
    stepsBarRef: any
};

interface Price {
    totalPrice: number,
    deliveryTodayPrice: number,
    floorPrice: number,
    garbagePackagePrice: number,
}

function fetchPriceFinal(apiUrl: string, city: string, size: string, deliveryDate: string, floor: number, bagsCount: number, signal: AbortSignal) {
    return new Promise<{
        finalPrice: Price
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'price/' + encodeURIComponent(city) + '/' + encodeURIComponent(size) + '/' + encodeURIComponent(deliveryDate) + '/' + floor + '/' + bagsCount, options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((finalPrice) => {
                resolve({finalPrice});
            })
            .catch(reject);
    });
}

export const ContactFormStep = ({
                                    setStep,
                                    isCompany,
                                    setIsCompany,
                                    name,
                                    setName,
                                    phone,
                                    setPhone,
                                    email,
                                    setEmail,
                                    companyName,
                                    setCompanyName,
                                    companyCode,
                                    setCompanyCode,
                                    taxCode,
                                    setTaxCode,
                                    deliveryDate,
                                    elevatorExist,
                                    deliveryTime,
                                    notes,
                                    trashNearHouse,
                                    floor,
                                    address,
                                    selectedCity,
                                    selectedSize,
                                    deliveryTimeLabel,
                                    bagsCount,
                                    infoAboutBags,
                                    apiUrl,
                                    trashesPhotos,
                                    trashStacksCount,
                                    tags,
                                    stepsBarRef

                                }: ContactFormModel) => {

    const [nameError, setNameError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [companyNameError, setCompanyNameError] = useState(false);
    const [companyCodeError, setCompanyCodeError] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [agreedToTermsError, setAgreedToTermsError] = useState(false);
    const requestAbortController = React.useRef<AbortController | null>(null);
    const [finalPrice, setFinalPrice] = useState<Price | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const contactFormRef = useRef(null);

    const termsUrl = window.apiData.termsUrl || '#';
    const privacyPolicyUrl = window.apiData.privacyPolicyUrl || '#';

    const fetchFinalPrice = () => {
        const controller = new AbortController();
        fetchPriceFinal(apiUrl, selectedCity, selectedSize, deliveryDate, floor, bagsCount, controller.signal)
            .then(({finalPrice}) => {
                setFinalPrice(finalPrice);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        requestAbortController.current = controller;
    };


    const handleBackClick = () => {
        setStep(3);
    };

    const handleBuyerSelection = (event: any) => {
        setIsCompany(event.currentTarget.getAttribute("data-value") === "company");
    };

    const handleBuyerSelectionKeyDown = (event: any) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBuyerSelection(event);
        }
    };

    const handleOrderSubmit = (event: any) => {
        event.preventDefault();

        if (submitting) {
            return;
        }

        if (finalPrice === null) {
            return;
        }

        const formDetails = {
            name: name,
            phone: phone,
            email: email,
            companyName: companyName,
            companyCode: companyCode,
            taxCode: taxCode,
            isCompany: isCompany,
            deliveryDate: deliveryDate,
            elevatorExist: elevatorExist,
            deliveryTime: deliveryTime,
            notes: notes,
            bagsCount: bagsCount,
            infoAboutBags: infoAboutBags,
            trashNearHouse: trashNearHouse,
            floor: floor,
            address: address,
            size: selectedSize,
            city: selectedCity,
            price: finalPrice.totalPrice,
            trashes: tags.join(', '),
            trashesPhotos: trashesPhotos,
            trashStacksCount: trashStacksCount
        };

        let valid = true;

        if (!isCompany) {
            if (!name || name.length == 0) {
                setNameError(true);
                valid = false;
            } else {
                setNameError(false);
            }

            if (!phone || phone.length == 0) {
                setPhoneError(true);
                valid = false;
            } else {
                setPhoneError(false);
            }
        }

        if (isCompany) {
            if (!phone || phone.length == 0) {
                setPhoneError(true);
                valid = false;
            } else {
                setPhoneError(false);
            }

            if (!companyName || companyName.length == 0) {
                setCompanyNameError(true);
                valid = false;
            } else {
                setCompanyNameError(false);
            }

            if (!companyCode || companyCode.length == 0) {
                setCompanyCodeError(true);
                valid = false;
            } else {
                setCompanyCodeError(false);
            }
        }

        if (!agreedToTerms) {
            setAgreedToTermsError(true);
            valid = false;
        } else {
            setAgreedToTermsError(false);
        }

        if (valid) {
            setSubmitting(true);
            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formDetails)
            };

            fetch(apiUrl + "create-order", options)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error(`HTTP error! status: ${res.status}`);
                    }
                    return res.json();
                })
                .then(() => {
                    setStep(5);
                })
                .catch((error) => {
                    console.error(error);
                    setSubmitting(false);
                });
        } else {
            contactFormRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    };

    React.useEffect(() => {
        fetchFinalPrice();
        // abort request on unmount
        return () => requestAbortController.current?.abort();
    }, []);

    return (
        <>
            <div id="contact-form-step" className="grid">
                <div className="col-50" ref={contactFormRef}>
                    <div>
                        <div className="info-block"><span>Patvirtinimas</span>
                        </div>
                        <h3>Patvirtinti užsakymą</h3>
                        <div className="field-group">
                            <p>Kas užsako?</p>
                            <div className={"segment"} role="radiogroup" aria-label="Kas užsako?">
                                <div className={!isCompany ? "segment-btn active" : "segment-btn"}
                                     onClick={handleBuyerSelection}
                                     onKeyDown={handleBuyerSelectionKeyDown}
                                     role="radio" aria-checked={!isCompany} tabIndex={0}
                                     data-value={"private"}>Privatus asmuo
                                </div>
                                <div className={isCompany ? "segment-btn active" : "segment-btn"}
                                     onClick={handleBuyerSelection}
                                     onKeyDown={handleBuyerSelectionKeyDown}
                                     role="radio" aria-checked={isCompany} tabIndex={0}
                                     data-value={"company"}>Įmonė
                                </div>
                            </div>
                        </div>

                        {!isCompany && (<div id="normal-user">
                            <div className="field-group">
                                <label htmlFor="first-name">Vardas</label>
                                <input style={nameError ? {borderColor: "red"} : {}} type="text" name="first-namesss"
                                       id="first-name" placeholder="Vardas" value={name}
                                       onChange={e => setName(e.target.value)}/>
                            </div>
                            <div className="field-group">
                                <label htmlFor="phone-number">Telefono numeris</label>
                                <input style={phoneError ? {borderColor: "red"} : {}} type="text" name="phone-number"
                                       id="phone-number" placeholder="+370..."
                                       value={phone} onChange={e => setPhone(e.target.value)}/>
                            </div>
                            <div className="field-group">
                                <label htmlFor="email">El. paštas (nebūtina)</label>
                                <input type="text" name="email" id="email" placeholder="vardas@pastas.lt" value={email}
                                       onChange={e => setEmail(e.target.value)}/>
                            </div>
                        </div>)}

                        {isCompany && (<div id="company">
                                <div className="field-group">
                                    <label htmlFor="company-name">Įmonės pavadinimas</label>
                                    <input style={companyNameError ? {borderColor: "red"} : {}} type="text"
                                           name="company-name" id="company-name"
                                           placeholder="Įmonės pavadinimas" value={companyName}
                                           onChange={e => setCompanyName(e.target.value)}/>
                                </div>
                                <div className="field-group">
                                    <label htmlFor="company-code">Įmonės kodas</label>
                                    <input style={companyCodeError ? {borderColor: "red"} : {}} type="text"
                                           name="company-code" id="company-code" placeholder="Įmonės kodas"
                                           value={companyCode}
                                           onChange={e => setCompanyCode(e.target.value)}/>
                                </div>
                                <div className="field-group">
                                    <label htmlFor="pvm-code">PVM kodas (nebūtina)</label>
                                    <input type="text" name="pvm-code" id="pvm-code" placeholder="PVM kodas"
                                           value={taxCode}
                                           onChange={e => setTaxCode(e.target.value)}/>
                                </div>
                                <div className="field-group">
                                    <label htmlFor="phone-number">Telefono numeris</label>
                                    <input style={phoneError ? {borderColor: "red"} : {}} type="text"
                                           name="phone-number"
                                           id="phone-number" placeholder="+370..."
                                           value={phone}
                                           onChange={e => setPhone(e.target.value)}/>
                                </div>
                                <div className="field-group">
                                    <label htmlFor="email">El. paštas (nebūtina)</label>
                                    <input type="text" name="email" id="email" placeholder="vardas@pastas.lt"
                                           value={email}
                                           onChange={e => setEmail(e.target.value)}/>
                                </div>
                            </div>
                        )}

                        <div className="note-box">
                            <h4>Kas vyks toliau?</h4>
                            <p>Po užsakymo vairuotojas susisieks su jumis per ~30 min. Pokalbio metu galutinai
                                suderinsite
                                atvykimo detales ir preliminarų atliekų kiekio vertinimą.</p>
                        </div>

                        <label className={agreedToTermsError ? "terms-agree terms-agree-error" : "terms-agree"}
                               htmlFor="agree-to-terms">
                            <input type="checkbox" id="agree-to-terms" checked={agreedToTerms}
                                   onChange={e => {
                                       setAgreedToTerms(e.target.checked);
                                       if (e.target.checked) {
                                           setAgreedToTermsError(false);
                                       }
                                   }}/>
                            <span>
                                <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                                    Sutinku su taisyklėmis
                                </a>
                                {' ir '}
                                <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                                    susipažinau su privatumo politika
                                </a>.
                            </span>
                        </label>
                    </div>
                </div>
                <div className="col-50">
                    <div>
                        <div className="info-block"><span>Santrauka</span>
                        </div>
                        <h3>Jūsų pasirinkimai</h3>
                        <div className="table">
                            <div className="table-row">
                                <span>Atliekų tipas</span>
                                <span><strong>{tags.join(", ")}</strong></span>
                            </div>
                            <div className="table-row">
                                <span>Preliminarus tūris</span>
                                <span><strong>{selectedSize}</strong></span>
                            </div>
                            <div className="table-row">
                                <span>Atliekų vieta</span>
                                <span><strong>

                                {(() => {
                                    switch (floor) {
                                        case 1:
                                            return 'Pirmas aukštas';
                                        case 2:
                                            return 'Antras aukštas';
                                        case 3:
                                            return 'Trečias aukštas';
                                        case 4:
                                            return '4+ aukštas';
                                    }
                                })()}
                            </strong></span>
                            </div>
                            {floor > 1 && (<div className="table-row">
                                    <span>Liftas</span>
                                    <span><strong>{elevatorExist ? "Taip" : "Ne"}</strong></span>
                                </div>
                            )}
                            {floor === 1 && (<div className="table-row">
                                    <span>Atliekos prie namo</span>
                                    <span><strong>{trashNearHouse ? 'Taip' : 'Ne'}</strong></span>
                                </div>
                            )}
                            <div className="table-row">
                                <span>Atvykimas</span>
                                <span><strong>
                                 {(() => {
                                     switch (deliveryDate) {
                                         case "today":
                                             return 'Šiandien';
                                         case "tomorrow":
                                             return 'Rytoj';
                                         default:
                                             return deliveryDate;
                                     }
                                 })()}
                            </strong></span>
                            </div>
                            <div className="table-row">
                                <span>Pageidaujamas laikas</span>
                                <span><strong>{deliveryTimeLabel}</strong></span>
                            </div>
                            <div className="table-row">
                                <span>Adresas</span>
                                <span><strong>{address}</strong></span>
                            </div>
                            <div className="table-row">
                                <span>Papildoma informacija</span>
                                <span><strong>{notes ? notes : "Nėra"}</strong></span>
                            </div>
                            <div className="table-row">
                                <span>Galutinė preliminari kaina</span>
                                <span><strong key={finalPrice?.totalPrice}
                                              className="price-pulse">{finalPrice !== null && finalPrice.totalPrice} €</strong></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mobile-sticky-footer">
                <div className="navigation-block mt20">
                    <button className="btn btn-secondary btn-normal btn-left" onClick={handleBackClick}>← Atgal
                    </button>
                    <button className="btn primary-btn btn-normal btn-right" onClick={handleOrderSubmit}
                            disabled={submitting || finalPrice === null}>Užsakyti</button>
                </div>
            </div>
        </>
    );
}
